import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import DrugScheduleService from "../../service/DrugScheduleService";
import DrugScheduleView from "./DrugScheduleView";
import { Sparkle } from "../controls";
import {
  DS_V2_ADD, DS_V2_UPDATE, DS_V2_REFRESH, DS_V2_EDIT_CAPTURED,
} from "./events";
import {
  STATUS, normaliseStatus, formatDateShort, drugNameOf, nextDoseFor,
  stagedAddToPlan,
} from "./helpers";
import "../../styles/drugSchedule.scss";

/**
 * Drug schedule — patient-page section. Middle-column content: table of
 * per-drug courses with dose-by-dose adherence. Add/Update/View forms
 * live on the right-rail editor (see DrugScheduleEditor.js), reached
 * via EventService pings (DS_V2_ADD/UPDATE/VIEW).
 *
 * Zero backend model change: existing pharmacy-dose-schedule-api handles
 * every field the mockup needs. Drug name reads from `doses[0].dosename`
 * (existing per-dose field, always populated in prod entries).
 * Instruction-for-planner is re-fetched from `/drug` catalog on
 * Add/View by matching drug name — deliberately no snapshot so the
 * latest medical guidance is always shown.
 */

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
    stroke="currentColor" strokeWidth="1.6"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3.5" y="5" width="17" height="15" rx="2" />
    <path d="M8 3v4M16 3v4M3.5 10h17" />
    <circle cx="16" cy="15.5" r="1.6" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 16 16" width="14" height="14" fill="none"
    stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M8 3v10M3 8h10" />
  </svg>
);

const ChipCaret = () => (
  <svg viewBox="0 0 10 6" width="10" height="6" fill="none"
    stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 1l4 4 4-4" />
  </svg>
);

/** Render a value, or a MUTED em-dash when there is nothing to show
 *  (Figma 4473:15741 — the empty dose cells are a lighter grey than a
 *  real value, which a bare text node cannot express).
 *
 *  `formatDateShort` already answers "—" for unusable input, so that is
 *  treated as absent too: otherwise a bad date slips through the happy
 *  branch and renders an unmuted dash. */
const orDash = (value) =>
  !value || value === "\u2014" ? <span className="ds-dash">&mdash;</span> : value;

/** Status chip renderer. Only Active carries a caret + click affordance:
 *  Completed / Expired are terminal states and can't be transitioned
 *  from (locked per spec — care manager creates a new schedule for the
 *  same drug instead of resurrecting a closed one). */
const StatusChip = ({ status, onClick, disabled }) => {
  const s = normaliseStatus(status);
  const clickable = s === STATUS.ACTIVE && onClick && !disabled;
  const cls =
    s === STATUS.ACTIVE    ? "ds-chip is-active"    :
    s === STATUS.COMPLETED ? "ds-chip is-completed" :
    s === STATUS.EXPIRED   ? "ds-chip is-expired"   :
                             "ds-chip";
  const label = s || "—";
  if (!clickable) {
    return <span className={cls}><span className="ds-chip-dot" />{label}</span>;
  }
  return (
    <button
      type="button"
      className={`${cls} ds-chip-btn`}
      onClick={onClick}
      title="Update actual dose dates or mark as Expired"
    >
      <span className="ds-chip-dot" />
      {label}
      <ChipCaret />
    </button>
  );
};
StatusChip.propTypes = {
  status:   PropTypes.string,
  onClick:  PropTypes.func,
  disabled: PropTypes.bool,
};

const DrugSchedule = ({ patient, capturedItems, transcriptMode }) => {
  const patientId = patient && (patient.id || patient.patientID);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // View-schedule modal is owned by the list component (not the editor
  // rail) so it opens as a centered dialog regardless of what the rail
  // is currently showing.
  const [viewPlan, setViewPlan] = useState(null);
  const service = useRef(new DrugScheduleService()).current;

  // Transcript-mode overlay. Captured items in state=added_in_call are
  // manual plans the reviewer just staged (or AI-captured drug schedule
  // atoms) — they show as peach-highlighted rows above the persisted
  // plans, with a sparkle marker for AI-originated captures. Nothing
  // hits the pharmacy DB until Review Save & next commits them.
  const stagedRows = useMemo(() => {
    if (!Array.isArray(capturedItems) || capturedItems.length === 0) return [];
    return capturedItems
      .filter((it) => it && it.targetSection === "drug_schedule"
        && (it.state === "added_in_call" || it.state === "edited_in_call")
        && (it.operation || "add") === "add")
      .map((it) => stagedAddToPlan(it));
  }, [capturedItems]);

  // Newest schedules first — mockup / user preference. Sort a copy so
  // the original server order stays untouched (in case anywhere reads
  // `plans` directly). Staged (transcript-mode) rows slot in at the
  // TOP so the reviewer sees their in-flight plans without scrolling.
  const sortedPlans = useMemo(
    () => {
      const server = plans.slice().sort((a, b) => {
        const aT = String(a && a.createdAt || "");
        const bT = String(b && b.createdAt || "");
        if (aT === bT) return 0;
        return aT < bT ? 1 : -1;
      });
      return [...stagedRows, ...server];
    },
    [plans, stagedRows],
  );

  const load = useCallback(() => {
    if (!patientId) {
      setPlans([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    service.list(
      patientId,
      (res) => {
        setPlans(res);
        setLoading(false);
      },
      (err) => {
        setError((err && err.message) || "Could not load drug schedules.");
        setLoading(false);
      },
    );
  }, [patientId, service]);

  useEffect(() => { load(); }, [load]);

  // Refetch when the rail saves / updates a schedule.
  useEffect(() => {
    EventService.addListener(DS_V2_REFRESH, load);
    return () => EventService.removeListener(DS_V2_REFRESH, load);
  }, [load]);

  const openAdd    = () => EventService.emit(DS_V2_ADD, { patient });
  const openUpdate = (plan) => EventService.emit(DS_V2_UPDATE, { plan, patient });
  // Transcript-mode: staged rows (not-yet-committed captured plans) open
  // the Add sheet pre-filled from their captured payload so the
  // reviewer can modify them without duplicating. Server rows in
  // transcript mode are read-only — see the render branches below.
  const openEditCaptured = (plan) =>
    EventService.emit(DS_V2_EDIT_CAPTURED, { plan, patient });
  // View opens as a modal owned by this component — no editor rail.
  const openView   = (plan) => setViewPlan(plan);

  if (loading) {
    return (
      <div className="ds-shell">
        <div className="ds-card ds-empty">
          <p className="ds-empty-line">Loading drug schedules…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ds-shell">
        <div className="ds-card ds-empty">
          <p className="ds-empty-title">Couldn’t load</p>
          <p className="ds-empty-sub">{error}</p>
          <button type="button" className="ds-cta" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (plans.length === 0 && stagedRows.length === 0) {
    return (
      <div className="ds-shell">
        <header className="ds-head">
          <h3 className="ds-title">Drug schedule
            <span className="ds-count"> · No entries</span>
          </h3>
        </header>
        <div className="ds-card ds-empty">
          <span className="ds-empty-icon" aria-hidden="true"><CalendarIcon /></span>
          <p className="ds-empty-title">No drug schedules yet</p>
          <p className="ds-empty-sub">
            Nothing has been planned so far. Create the first schedule to
            start tracking planned vs actual doses for this patient.
          </p>
          <button type="button" className="ds-cta" onClick={openAdd}>
            <PlusIcon />Add schedule
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ds-shell">
      <header className="ds-head">
        <h3 className="ds-title">
          Drug schedule
          <span className="ds-count"> · {sortedPlans.length} entr{sortedPlans.length === 1 ? "y" : "ies"}</span>
        </h3>
        <button type="button" className="ds-cta" onClick={openAdd}>
          <PlusIcon />Add schedule
        </button>
      </header>

      <div className="ds-tblcard">
        <div className="ds-tbl" role="table">
          <div className="ds-thead" role="row">
            <span role="columnheader" className="ds-cell ds-c-drug">DRUG</span>
            <span role="columnheader" className="ds-cell ds-c-when">CREATED ON</span>
            <span role="columnheader" className="ds-cell ds-c-who">CREATED BY</span>
            <span role="columnheader" className="ds-cell ds-c-updose">UPCOMING DOSE</span>
            <span role="columnheader" className="ds-cell ds-c-update">NEXT DOSE DATE</span>
            <span role="columnheader" className="ds-cell ds-c-status">STATUS</span>
            <span role="columnheader" className="ds-cell ds-c-view">SCHEDULE</span>
          </div>
          {sortedPlans.map((plan) => {
            const s = normaliseStatus(plan.status);
            const active = s === STATUS.ACTIVE;
            const next = active ? nextDoseFor(plan) : null;
            const total = Array.isArray(plan.doses) ? plan.doses.length : 0;
            const id = plan.id || plan._id || `${plan.pid}-${plan.createdAt}`;
            // Staged (transcript-mode) rows are not yet in the pharmacy
            // DB — dim the STATUS chip / disable View + Update clicks
            // and paint the row peach with a sparkle if AI-captured.
            const isStaged = !!plan._isStaged;
            const isAi = !!plan._isAi;
            const rowCls = `ds-trow${isStaged ? " is-staged" : ""}${isAi ? " has-sparkle" : ""}`;
            return (
              <div className={rowCls} role="row" key={String(id)}>
                <span role="cell" className="ds-cell ds-c-drug">
                  {isAi ? (
                    <span className="cc-spark" aria-label="AI captured" title="AI captured">
                      <Sparkle aria-hidden />
                    </span>
                  ) : null}
                  {/* Row-click rules:
                       • Staged row (transcript, not yet committed) —
                         click reopens the Add sheet pre-filled from the
                         captured payload so the reviewer can modify it
                         in place.
                       • Server row in transcript mode — read-only; the
                         reviewer can only add NEW schedules from the
                         header CTA. Existing entries are frozen.
                       • Server row in standalone mode — click opens the
                         Update sheet. */}
                  {isStaged ? (
                    <button
                      type="button"
                      className="ds-name-btn ds-drugname"
                      onClick={() => openEditCaptured(plan)}
                      title="Edit this staged schedule"
                    >
                      {orDash(drugNameOf(plan))}
                    </button>
                  ) : transcriptMode ? (
                    <span
                      className="ds-drugname"
                      title="Editing existing schedules is disabled during transcript review"
                    >
                      {orDash(drugNameOf(plan))}
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="ds-name-btn ds-drugname"
                      onClick={() => openUpdate(plan)}
                      title="Update schedule"
                    >
                      {orDash(drugNameOf(plan))}
                    </button>
                  )}
                </span>
                <span role="cell" className="ds-cell ds-c-when">
                  {orDash(formatDateShort((plan.createdAt || "").slice(0, 10)))}
                </span>
                <span role="cell" className="ds-cell ds-c-who">
                  {orDash(plan.createdby)}
                </span>
                <span role="cell" className="ds-cell ds-c-updose">
                  {orDash(next ? `Dose ${next.doseNo} of ${total}` : "")}
                </span>
                <span role="cell" className="ds-cell ds-c-update">
                  {orDash(next ? formatDateShort(next.plannedDate) : "")}
                </span>
                <span role="cell" className="ds-cell ds-c-status">
                  {/* STATUS never opens the update sheet in transcript
                      mode — same lockdown as the DRUG column. Staged
                      rows are also frozen here since their status only
                      lands once Review Save & next commits them. */}
                  <StatusChip
                    status={plan.status}
                    onClick={isStaged || transcriptMode ? undefined : () => openUpdate(plan)}
                    disabled={isStaged || transcriptMode}
                  />
                </span>
                <span role="cell" className="ds-cell ds-c-view">
                  {isStaged ? (
                    <span className="ds-view-pending">Pending review</span>
                  ) : (
                    <button
                      type="button"
                      className="ds-view-link"
                      onClick={() => openView(plan)}
                    >
                      View schedule
                    </button>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* View-schedule modal, opened from the row's "View schedule" link.
          Rendered here (not in the editor rail) so it stacks as a proper
          centered dialog regardless of what the rail is showing. */}
      {viewPlan ? (
        <DrugScheduleView
          plan={viewPlan}
          onClose={() => setViewPlan(null)}
        />
      ) : null}
    </div>
  );
};

DrugSchedule.propTypes = {
  patient:       PropTypes.object,
  /** Transcript-mode captured items (from AI + manual add cards).
   *  Absent / empty on the standalone patient page. */
  capturedItems: PropTypes.array,
  /** True when this section is rendered inside the AI transcript modal.
   *  Locks existing (server) rows to read-only and disables the STATUS
   *  chip everywhere; only Add + editing staged rows stay available. */
  transcriptMode: PropTypes.bool,
};
DrugSchedule.defaultProps = { capturedItems: [], transcriptMode: false };

export default DrugSchedule;
